import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { resetPassword } from './authSlice';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import { useTheme } from '@mui/material/styles';

const PasswordResetModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setMessage('メールアドレスを入力してください');
      setStatus('error');
      return;
    }

    setStatus('loading');
    try {
      await dispatch(resetPassword({ email })).unwrap();
      setStatus('success');
      setMessage(`${email} にパスワードリセットメールを送信しました`);
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error) {
      setStatus('error');
      setMessage(error);
    }
  };

  const handleClose = () => {
    setEmail('');
    setStatus('idle');
    setMessage('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 0,
          bgcolor: 'rgba(37, 37, 48, 0.98)',
          border: `2px solid ${theme.palette.neon.cyan}`,
          boxShadow: `0 0 30px ${theme.palette.neon.cyan}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: `linear-gradient(90deg, transparent, ${theme.palette.neon.cyan}, transparent)`,
            animation: 'scan 3s linear infinite',
          },
          '@keyframes scan': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          textAlign: 'center',
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: theme.palette.neon.cyan,
          textShadow: `0 0 20px ${theme.palette.neon.cyan}`,
          position: 'relative',
          pr: 6,
        }}
      >
        パスワードリセット
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.neon.cyan,
            '&:hover': {
              transform: 'rotate(90deg)',
              color: theme.palette.neon.pink,
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <EmailIcon 
            sx={{ 
              fontSize: 60, 
              color: theme.palette.neon.cyan,
              mb: 2,
              filter: `drop-shadow(0 0 20px ${theme.palette.neon.cyan})`,
            }} 
          />
          
          {status === 'idle' && (
            <>
              <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
                登録されているメールアドレスを入力してください。
                パスワードリセット用のリンクをお送りします。
              </Typography>
              
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="メールアドレス"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="email"
                  autoFocus
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      backgroundColor: 'rgba(26, 26, 31, 0.6)',
                      '& fieldset': {
                        borderColor: 'rgba(0, 255, 255, 0.3)',
                        borderWidth: 2,
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 255, 255, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.neon.cyan,
                        boxShadow: `0 0 10px ${theme.palette.neon.cyan}`,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontWeight: 600,
                      letterSpacing: '0.05em',
                      '&.Mui-focused': {
                        color: theme.palette.neon.cyan,
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      color: '#ffffff',
                      fontWeight: 500,
                    },
                  }}
                />
              </form>
            </>
          )}
          
          {status === 'loading' && (
            <Box sx={{ py: 4 }}>
              <CircularProgress 
                size={60} 
                sx={{ 
                  color: theme.palette.neon.cyan,
                  filter: `drop-shadow(0 0 10px ${theme.palette.neon.cyan})`,
                }} 
              />
              <Typography variant="body1" sx={{ mt: 2 }}>
                送信中...
              </Typography>
            </Box>
          )}
          
          {(status === 'success' || status === 'error') && (
            <Alert 
              severity={status}
              sx={{ 
                mt: 2,
                bgcolor: status === 'success' 
                  ? 'rgba(0, 255, 0, 0.1)' 
                  : 'rgba(255, 0, 102, 0.1)',
                color: status === 'success' 
                  ? theme.palette.neon.lime 
                  : theme.palette.neon.pink,
                border: `1px solid ${status === 'success' 
                  ? theme.palette.neon.lime 
                  : theme.palette.neon.pink}`,
                borderRadius: 0,
                '& .MuiAlert-icon': {
                  color: status === 'success' 
                    ? theme.palette.neon.lime 
                    : theme.palette.neon.pink,
                }
              }}
            >
              {message}
            </Alert>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        {status === 'idle' && (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderRadius: 0,
                borderColor: theme.palette.neon.magenta,
                borderWidth: 2,
                color: theme.palette.neon.magenta,
                backgroundColor: 'rgba(255, 0, 255, 0.05)',
                fontWeight: 700,
                letterSpacing: '0.05em',
                px: 3,
                '&:hover': {
                  borderColor: theme.palette.neon.magenta,
                  borderWidth: 2,
                  backgroundColor: 'rgba(255, 0, 255, 0.1)',
                  boxShadow: `0 0 20px ${theme.palette.neon.magenta}`,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              キャンセル
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              sx={{ 
                ml: 2,
                borderRadius: 0,
                background: `linear-gradient(45deg, ${theme.palette.neon.cyan} 0%, ${theme.palette.neon.magenta} 100%)`,
                boxShadow: `0 0 20px ${theme.palette.neon.cyan}`,
                border: `2px solid ${theme.palette.neon.cyan}`,
                fontWeight: 700,
                letterSpacing: '0.1em',
                fontSize: '1rem',
                textTransform: 'uppercase',
                px: 3,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  transition: 'left 0.5s ease',
                },
                '&:hover::before': {
                  left: '100%',
                },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 0 30px ${theme.palette.neon.cyan}`,
                },
              }}
            >
              送信
            </Button>
          </>
        )}
        
        {status === 'success' && (
          <Button
            onClick={handleClose}
            variant="contained"
            sx={{ 
              borderRadius: 0,
              background: `linear-gradient(45deg, ${theme.palette.neon.lime} 0%, ${theme.palette.neon.cyan} 100%)`,
              boxShadow: `0 0 20px ${theme.palette.neon.lime}`,
              border: `2px solid ${theme.palette.neon.lime}`,
              fontWeight: 700,
              letterSpacing: '0.1em',
              px: 3,
            }}
          >
            閉じる
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PasswordResetModal;
